import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import { sleep } from './utils'

import club from '@/data/club.json'
import programs from '@/data/programs.json'
import teams from '@/data/teams.json'
import coaches from '@/data/coaches.json'
import camps from '@/data/camps.json'
import gallery from '@/data/gallery.json'
import sponsors from '@/data/sponsors.json'
import uniforms from '@/data/uniforms.json'
import stats from '@/data/stats.json'
import seasonStats from '@/data/season-stats.json'
import jamaicaCoaches from '@/data/jamaica-coaches.json'
import testimonials from '@/data/testimonials.json'
import videos from '@/data/videos.json'
import heroSlides from '@/data/hero-slides.json'

/* ---------------- axios ---------------- */

const baseURL = import.meta.env.VITE_API_BASE ?? ''
const useMock = import.meta.env.VITE_USE_MOCK !== 'false'

export const api = axios.create({
  baseURL,
  timeout: 8000,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('cosmos-token')
  if (token) cfg.headers.Authorization = `Bearer ${token}`
  return cfg
})

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    const status = err.response?.status ?? 0
    const body = err.response?.data as { message?: string } | undefined
    const message = body?.message ?? err.message ?? 'Request failed'
    if (status >= 500) toast.error(`Server error (${status}) — please try again.`)
    else if (status === 0) toast.error(`Network error — check your connection.`)
    return Promise.reject({ status, message, raw: err })
  }
)

/* ---------------- mock adapter ---------------- */

type MockHandler = (params: Record<string, string>, body?: unknown) => unknown

const routes: Array<{ method: string; pattern: RegExp; keys: string[]; handler: MockHandler }> = []

function register(method: string, path: string, handler: MockHandler) {
  const keys: string[] = []
  const pattern = new RegExp(
    '^' +
      path.replace(/:([a-zA-Z]+)/g, (_, k) => {
        keys.push(k)
        return '([^/]+)'
      }) +
      '/?$'
  )
  routes.push({ method: method.toUpperCase(), pattern, keys, handler })
}

/* GET routes */
register('GET', '/api/club', () => club)
register('GET', '/api/programs', () => programs)
register('GET', '/api/programs/:slug', ({ slug }) => {
  const found = programs.find((p) => p.slug === slug)
  if (!found) throw { status: 404, message: `Program "${slug}" not found` }
  return found
})
register('GET', '/api/teams', () => teams)
register('GET', '/api/teams/:division', ({ division }) => {
  const div = teams[division as 'boys' | 'girls']
  if (!div) throw { status: 404, message: `Division "${division}" not found` }
  return div
})
register('GET', '/api/teams/:division/:id', ({ division, id }) => {
  const div = teams[division as 'boys' | 'girls']
  const t = div?.find((x) => x.id === id)
  if (!t) throw { status: 404, message: `Team not found` }
  return t
})
register('GET', '/api/coaches', () => coaches)
register('GET', '/api/coaches/:slug', ({ slug }) => {
  const c = coaches.find((x) => x.slug === slug)
  if (!c) throw { status: 404, message: `Coach "${slug}" not found` }
  return c
})
register('GET', '/api/camps', () => camps)
register('GET', '/api/camps/:slug', ({ slug }) => {
  const c = camps.find((x) => x.slug === slug)
  if (!c) throw { status: 404, message: `Camp "${slug}" not found` }
  return c
})
register('GET', '/api/gallery', () => gallery)
register('GET', '/api/sponsors', () => sponsors)
register('GET', '/api/uniforms', () => uniforms)
register('GET', '/api/stats', () => stats)
register('GET', '/api/season-stats/:teamId', ({ teamId }) => {
  const s = (seasonStats as Record<string, unknown>)[teamId] ?? (seasonStats as Record<string, unknown>)['default']
  return s
})
register('GET', '/api/jamaica-coaches', () => jamaicaCoaches)
register('GET', '/api/testimonials', () => testimonials)
register('GET', '/api/videos', () => videos)
register('GET', '/api/hero-slides', () => heroSlides)

/* POST routes (mock success) */
const post = (path: string) =>
  register('POST', path, (_p, body) => {
    console.info(`[MOCK POST] ${path}`, body)
    return { ok: true, submittedAt: new Date().toISOString() }
  })
post('/api/register')
post('/api/contact')
post('/api/scholarship')
post('/api/donate')

/* attach mock adapter */
if (useMock) {
  api.defaults.adapter = async (config) => {
    const method = (config.method ?? 'get').toUpperCase()
    const url = (config.url ?? '').replace(config.baseURL ?? '', '')

    // simulate latency
    await sleep(150 + Math.random() * 250)

    for (const r of routes) {
      if (r.method !== method) continue
      const match = url.match(r.pattern)
      if (!match) continue
      const params: Record<string, string> = {}
      r.keys.forEach((k, i) => (params[k] = decodeURIComponent(match[i + 1] ?? '')))
      try {
        const body = config.data ? JSON.parse(config.data as string) : undefined
        const data = r.handler(params, body)
        return {
          data,
          status: 200,
          statusText: 'OK',
          headers: {},
          config,
        } as AxiosResponse
      } catch (err) {
        const e = err as { status?: number; message?: string }
        return Promise.reject({
          response: {
            status: e.status ?? 500,
            statusText: 'Error',
            data: { message: e.message ?? 'Mock error' },
            headers: {},
            config,
          },
          message: e.message ?? 'Mock error',
          isAxiosError: true,
          toJSON: () => ({}),
        })
      }
    }

    return Promise.reject({
      response: {
        status: 404,
        statusText: 'Not Found',
        data: { message: `Mock: no route for ${method} ${url}` },
        headers: {},
        config,
      },
      message: `Mock 404: ${method} ${url}`,
      isAxiosError: true,
      toJSON: () => ({}),
    })
  }
}

/* ---------------- typed helpers ---------------- */

export async function apiGet<T>(path: string) {
  const r = await api.get<T>(path)
  return r.data
}

export async function apiPost<T, B = unknown>(path: string, body?: B) {
  const r = await api.post<T>(path, body)
  return r.data
}
